import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import TableCompo from "../Table/TableCompo";
import { fetchSipPlansRequest } from "../../../redux/SipPlan/SipPlanSlice";

export default function SipPlanGroupPlans() {
  const { parentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { sipPlans = [], loading } = useSelector((state) => state.sipplan);

  const [pagination, setPagination] = useState({ page: 0, limit: 50 });
  const [searchText, setSearchText] = useState("");

  // Fetch a large set so we can build the chain client-side.
  useEffect(() => {
    dispatch(fetchSipPlansRequest({ page: 1, limit: 1000, search: "" }));
  }, [dispatch]);

  const handleTableChange = (action, payload) => {
    switch (action) {
      case "search":
        setSearchText(payload.searchText || "");
        setPagination((prev) => ({ ...prev, page: 0 }));
        break;
      case "changePage":
        setPagination((prev) => ({ ...prev, page: payload.page }));
        break;
      case "changeRowsPerPage":
        setPagination({ page: 0, limit: payload.rowsPerPage });
        break;
      default:
        break;
    }
  };

  const chainPlans = useMemo(() => {
    const all = Array.isArray(sipPlans) ? sipPlans : [];
    const byId = new Map(all.map((p) => [String(p?._id), p]));
    const childrenByParent = new Map();

    for (const p of all) {
      const parent = p?.parentPlanId;
      const key =
        typeof parent === "string"
          ? parent
          : parent && typeof parent === "object"
            ? parent?._id
            : null;
      if (!key) continue;
      const k = String(key);
      if (!childrenByParent.has(k)) childrenByParent.set(k, []);
      childrenByParent.get(k).push(p);
    }

    const rootId = String(parentId || "");
    const root = byId.get(rootId);
    const result = [];

    // If the root isn't in the fetched list, we still try to show descendants.
    const queue = [rootId];
    const visited = new Set();

    if (root) result.push(root);

    while (queue.length) {
      const current = queue.shift();
      if (!current || visited.has(current)) continue;
      visited.add(current);

      const kids = childrenByParent.get(String(current)) || [];
      for (const child of kids) {
        if (!result.some((x) => String(x?._id) === String(child?._id))) {
          result.push(child);
        }
        queue.push(String(child?._id));
      }
    }

    const filtered = searchText
      ? result.filter((p) =>
          String(p?.name || "")
            .toLowerCase()
            .includes(String(searchText).toLowerCase()),
        )
      : result;

    return filtered.sort((a, b) => {
      const ga = Number(a?.groupNumber ?? 0);
      const gb = Number(b?.groupNumber ?? 0);
      if (ga !== gb) return ga - gb;
      return Number(a?.createdAt ?? 0) - Number(b?.createdAt ?? 0);
    });
  }, [sipPlans, parentId, searchText]);

  const pagedData = useMemo(() => {
    const start = pagination.page * pagination.limit;
    const end = start + pagination.limit;
    return chainPlans.slice(start, end);
  }, [chainPlans, pagination]);

  const columns = useMemo(
    () => [
      { field: "_id", headerName: "Id", hide: true },
      { field: "name", headerName: "Name", minWidth: 220 },
      {
        field: "autoGroupCreation",
        headerName: "Auto Group Creation",
        render: (value) => (value ? "Yes" : "No"),
      },
      {
        field: "maxSipLimit",
        headerName: "Max Customer Limit",
      },
      {
        field: "maxSipGroup",
        headerName: "Max SIP Group",
        render: (value, row) =>
          row?.autoGroupCreation ? value ?? "-" : "N.A.",
      },
      {
        field: "groupNumber",
        headerName: "Group No",
        render: (value, row) =>
          row?.autoGroupCreation ? value ?? "-" : "N.A.",
      },
      {
        field: "parentPlanId",
        headerName: "Parent SIP",
        minWidth: 180,
        render: (value, row) => {
          if (!row?.autoGroupCreation) return "N.A.";
          return (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" component="span">
                {!value ? "Parent" : "Child"}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "enabled",
        headerName: "Status",
        render: (value) => (value ? "Enabled" : "Disabled"),
      },
      {
        field: "createdAt",
        headerName: "Created At",
        minWidth: 220,
        render: (value) => {
          const date = new Date(value);
          return isNaN(date.getTime())
            ? "-"
            : new Intl.DateTimeFormat("en-IN", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              }).format(date);
        },
      },
      {
        field: "_id-viewSips",
        headerName: "View SIP",
        minWidth: 140,
        hideInExcel: true,
        render: (val, row) => (
          <button
            onClick={() => {
              navigate(`/SipSubscriberList?id=${row?._id}`);
            }}
            style={{
              backgroundColor: "#ffc107",
              color: "#000",
              border: "none",
              padding: "5px 10px",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            View
          </button>
        ),
      },
      {
        field: "runningSipCount",
        headerName: "Running SIPs",
        minWidth: 140,
        hideInExcel: true,
        render: (value, row) => {
          if (row?.commonSips && Array.isArray(row.commonSips)) {
            const count = row.commonSips.filter(
              (sip) => sip.status === "RUNNING",
            ).length;
            return count > 0 ? count : "0";
          }
          return "0";
        },
      },
      {
        field: "completedSipCount",
        headerName: "Completed SIPs",
        minWidth: 150,
        hideInExcel: true,
        render: (value, row) => {
          if (row?.commonSips && Array.isArray(row.commonSips)) {
            const count = row.commonSips.filter(
              (sip) => sip.status === "COMPLETED",
            ).length;
            return count > 0 ? count : "0";
          }
          return "0";
        },
      },
    ],
    [dispatch, navigate],
  );

  return (
    <Box
      sx={{
        p: 2,
        // Prevent content from hiding behind the app bar/header
        pt: { xs: 10, sm: 12 },
        pb: { xs: 6, sm: 4 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography fontWeight={700} fontSize={20} fontFamily={"Aeonik"}>
            SIP Plan Group List
          </Typography>
          <Typography sx={{ color: "#606778" }} fontSize={14}>
            Parent Plan Id: {parentId}
          </Typography>
        </Box>

        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>

      <TableCompo
        title="SIP Group"
        columns={columns}
        data={pagedData}
        totalCount={chainPlans.length}
        loading={loading}
        page={pagination.page}
        limit={pagination.limit}
        onTableChange={handleTableChange}
        stickyColumnFields={["name"]}
      />
    </Box>
  );
}

